import { useContext, useEffect, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { FieldOptions } from '../../../types/QueryBuilder';
import { QueryBuilderContext } from '../context';
import { inferFieldType } from '../utils';

import { FilterValueAutocomplete } from './FilterValueAutocomplete';

import styles from './FieldValuePanel.module.css';

export function isMultiValue(field: FieldOptions): boolean {
  return !!field.supportsMultipleValues || !!field.multi;
}

function isBooleanField(field: FieldOptions): boolean {
  if (field.valueType) {
    return field.valueType === 'boolean';
  }
  const samples = field.sampleValues ?? [];
  return samples.length > 0 && inferFieldType(samples) === 'BOOLEAN';
}

export interface FieldValuePanelProps {
  field: FieldOptions;
  initialValues: Array<string | number | boolean>;
  onApply: (values: Array<string | number | boolean>) => void;
  onValuesChange?: (values: Array<string | number | boolean>) => void;
  immediate?: boolean;
  singleSelect?: boolean;
  onClose?: () => void;
  onDismiss?: () => void;
}

export function FieldValuePanel({
  field,
  initialValues,
  onApply,
  onValuesChange,
  immediate = false,
  singleSelect = false,
  onClose,
  onDismiss,
}: FieldValuePanelProps) {
  const multi = isMultiValue(field) && !singleSelect;
  const boolean = isBooleanField(field);
  const [staged, setStaged] = useState<Array<string | number | boolean>>(initialValues);
  const [searchText, setSearchText] = useState('');

  // Read directly so the panel still works outside a QueryBuilder (stories, isolated tests).
  const queryBuilder = useContext(QueryBuilderContext);
  const setActiveValueField = queryBuilder?.setActiveValueField;
  const setActiveValueSearch = queryBuilder?.setActiveValueSearch;
  const fieldKey = field.field;
  useEffect(() => {
    setActiveValueField?.(fieldKey);
    return () => setActiveValueField?.(undefined);
  }, [setActiveValueField, fieldKey]);

  useEffect(() => {
    setActiveValueSearch?.(searchText);
    return () => setActiveValueSearch?.(undefined);
  }, [setActiveValueSearch, searchText]);

  const initialKey = JSON.stringify(initialValues);
  const [seededKey, setSeededKey] = useState(initialKey);
  if (initialKey !== seededKey) {
    setSeededKey(initialKey);
    setStaged(initialValues);
  }
  const showApply = multi && !immediate;
  const hasValues = (showApply ? staged : initialValues).length > 0;
  const clearValues =
    field.clearValuesLabel && hasValues ? (
      <Button
        variant="minimal"
        size="small"
        className={styles.clearValues}
        onPress={() => {
          setStaged([]);
          if (showApply) {
            onApply([]);
          } else {
            onValuesChange?.([]);
          }
          onClose?.();
        }}
      >
        <Icon name="cancel-circle-outline" size="small" slot="start-icon" />
        {field.clearValuesLabel}
      </Button>
    ) : null;

  if (field.renderValueSelector) {
    // Mirror the built-in autocomplete's staging contract: when the selection is staged
    // (multi-select, not `immediate`), edits accumulate locally and only commit via Apply.
    // Otherwise each change is written straight through.
    return (
      <div className={styles.panel}>
        {field.renderValueSelector({
          field,
          values: showApply ? staged : initialValues,
          onChange: (values) => (showApply ? setStaged(values) : onValuesChange?.(values)),
          onClose: () => onClose?.(),
          immediate,
        })}
        {showApply && (
          <Button className={styles.applyButton} onPress={() => onApply(staged)} isDisabled={staged.length === 0}>
            {msg('queryBuilder.filterValues.apply')}
          </Button>
        )}
        {clearValues}
      </div>
    );
  }

  const options = (field.sampleValues ?? []).map((sample) => ({
    value: sample.value,
    displayName: sample.displayValue,
    resultsCount: sample.approxCount || undefined,
  }));

  const toggle = (value: string | number | boolean) => {
    const key = String(value);
    const next = staged.some((v) => String(v) === key) ? staged.filter((v) => String(v) !== key) : [...staged, value];
    setStaged(next);
    if (immediate) {
      onValuesChange?.(next);
    }
  };

  return (
    <div className={styles.panel}>
      <FilterValueAutocomplete
        options={options}
        inputValue={searchText}
        onInputChange={setSearchText}
        multiSelect={multi}
        selectedValues={multi ? staged : undefined}
        onSelect={(value) => (multi ? toggle(value) : onApply([value]))}
        autoFocus
        showSearchField={!boolean}
        placeholder={msg('queryBuilder.filterValues.searchPlaceholder', {
          field: (field.displayValue ?? field.field).toLowerCase(),
        })}
        aria-label={msg('queryBuilder.filterValues.valuesLabel', { field: field.displayValue ?? field.field })}
        onDismiss={onDismiss}
        isLoading={field.isLoadingValues}
      />
      {showApply && (
        <Button className={styles.applyButton} onPress={() => onApply(staged)} isDisabled={staged.length === 0}>
          {msg('queryBuilder.filterValues.apply')}
        </Button>
      )}
      {clearValues}
    </div>
  );
}
